import { ReactNode } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  PieLabelRenderProps,
} from "recharts";
import { CSS } from "~/application/theme";
import { Card } from "~/components/Card";
import { Flex } from "~/components/Flex";
import { Text } from "~/components/Text";
import useMobile from "~/presentation/shared/hooks/useMobile";
import { colors as themeColors } from "~/application/theme";

export type DonutLegendPosition =
  | "bottom"
  | "right"
  | "top"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export interface DonutChartProps {
  data: { name: string; value: number }[];
  title?: ReactNode;
  subtitle?: ReactNode;
  colors?: string[];
  legendPosition?: DonutLegendPosition;
  height?: number;
  innerRadius?: number | string;
  outerRadius?: number | string;
  css?: CSS;
}

const DEFAULT_COLORS = [themeColors["primary-700"], themeColors["primary-100"]];

const CustomTooltip = ({ active, payload, colors, data }: any) => {
  if (!active || !payload || !payload.length) return null;

  const payloadData = payload[0]?.payload;
  const dataIndex = data?.findIndex((item: any) => item.name === payloadData?.name) ?? 0;
  const background = colors?.[dataIndex] || colors?.[0] || "#0D47A1";

  return (
    <Card css={{ p: "$2", background, color: "#fff", border: "none" }}>
      <Flex gap="1">
        <Text>{payload[0].name}</Text>: <Text fw="700">{payload[0].value}%</Text>
      </Flex>
    </Card>
  );
};

export function DonutChart({
  data,
  title,
  subtitle,
  colors = DEFAULT_COLORS,
  legendPosition = "right",
  height = 240,
  innerRadius = "55%",
  outerRadius = "75%",
  css,
}: DonutChartProps) {
  const filtered = data?.filter((d) => d?.value > 0) || [];
  const isMobile = useMobile();

  const renderCustomLabel = (props: PieLabelRenderProps) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, value } = props;

    const nCx = Number(cx);
    const nCy = Number(cy);
    const nInnerRadius = Number(innerRadius);
    const nOuterRadius = Number(outerRadius);
    const nMidAngle = Number(midAngle);

    if (!value) return null;

    const RADIAN = Math.PI / 180;
    const radius = nInnerRadius + (nOuterRadius - nInnerRadius) * 0.5;

    const x = nCx + radius * Math.cos(-nMidAngle * RADIAN);
    const y = nCy + radius * Math.sin(-nMidAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#FFFFFF"
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: "12px",
          fontWeight: "700",
          fontFamily: "inherit",
          pointerEvents: "none",
        }}
      >
        {`${value}%`}
      </text>
    );
  };

  const legendMap: Record<
    DonutLegendPosition,
    {
      layout: "horizontal" | "vertical";
      align: "left" | "center" | "right";
      verticalAlign: "top" | "middle" | "bottom";
      margin?: { top?: number; bottom?: number; left?: number; right?: number };
    }
  > = {
    right: { layout: "vertical", align: "right", verticalAlign: "middle", margin: { left: 10 } },
    top: { layout: "horizontal", align: "center", verticalAlign: "top", margin: { bottom: 10 } },
    bottom: { layout: "horizontal", align: "center", verticalAlign: "bottom", margin: { top: 10 } },
    "top-left": {
      layout: "horizontal",
      align: "left",
      verticalAlign: "top",
      margin: { bottom: 10 },
    },
    "top-right": {
      layout: "horizontal",
      align: "right",
      verticalAlign: "top",
      margin: { bottom: 10 },
    },
    "bottom-left": {
      layout: "horizontal",
      align: "left",
      verticalAlign: "bottom",
      margin: { top: 10 },
    },
    "bottom-right": {
      layout: "horizontal",
      align: "right",
      verticalAlign: "bottom",
      margin: { top: 10 },
    },
  };

  const legendCfg = legendMap[legendPosition] || legendMap.bottom;

  return (
    <Flex direction="column" css={{ width: "100%", height: "100%" }}>
      {typeof title === "string" ? (
        <Text css={{ mb: "$1", fw: "600", fs: "$xl" }}>{title}</Text>
      ) : (
        title
      )}
      {subtitle}

      <Flex direction="column" css={{ width: "100%", height: "100%", ...css }}>
        {filtered.length === 0 ? (
          <Flex justify="center" align="center" css={{ height }}>
            <Text>Nenhum dado disponível</Text>
          </Flex>
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                dataKey="value"
                data={data}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={90}
                endAngle={-270}
                label={isMobile ? renderCustomLabel : false}
                labelLine={false}
              >
                {data?.map((entry, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} strokeWidth={0} />
                ))}
              </Pie>

              {!isMobile && <Tooltip content={<CustomTooltip colors={colors} data={data} />} />}

              <Legend
                iconType="circle"
                layout={legendCfg.layout}
                align={legendCfg.align}
                verticalAlign={legendCfg.verticalAlign}
                margin={legendCfg.margin}
                iconSize={8}
                formatter={(value) => (
                  <Text variant="neutral-500" size="3">
                    {value}
                  </Text>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Flex>
    </Flex>
  );
}
